import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  FIELD_INFO_TYPE_COMMIT,
  getFormGroupLikeWrapper,
} from '@blaze-runtime/engine-domain';
import { FormState, FormStateContainer } from '@blaze-shared/elements-domain';
import { isDefined } from '@blaze-shared/shared-util';

import { refreshFormGroup } from '../../../+state/elements/elements.actions';
import { getElementById, getElementEntities } from '../../../+state/elements/elements.selectors';
import { hasWrapperRefs } from '../../../canvas-elements/element-model/element-props-guards';
import {
  isFormGroup,
  isFormGroupLike,
  isVariantOfButton,
} from '../../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../../canvas-elements/element-model/wrapper-refs';

/** A form the group commits, paired with the state it is in right now. */
interface GroupedFormValidity<TType extends string> {
  id: CanvasElementId;
  type: TType;
  state: FormState<FormStateContainer> | undefined;
}

/**
 * The validity of every form a group (or wizard) commits at once. Main forms are the ones
 * the group writes first; related forms are those keyed off a main form's row.
 */
export interface FormGroupValidity {
  containerId: CanvasElementId;
  containerType: 'formGroup' | 'wizard';
  mainForms: GroupedFormValidity<'formContainer' | 'formDetails'>[];
  relatedForms: GroupedFormValidity<'formContainer' | 'editableList'>[];
}

/**
 * Streams the combined validity of a form group's forms, so the group's commit button can
 * reject a submission that any one of them fails.
 */
@Injectable({ providedIn: 'root' })
export class FormGroupValidityService {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  /**
   * @param element the form group itself, or the button that commits it
   */
  observeFormGroupValidity(element: CanvasElement): Observable<FormGroupValidity> {
    return this.observeFormGroupContainer(element)
      .pipe(
        map((container) => {
          if (!isFormGroupLike(container)) {
            throw new Error('container is not form group like');
          }
          const wrapper = getFormGroupLikeWrapper(getWrapperRefs(container));
          const mainFormIds = wrapper
            .filter(({ keyColumnId }) => !keyColumnId)
            .map(({ formId }) => formId);
          const relatedFormIds = wrapper
            .filter(({ keyColumnId }) => keyColumnId)
            .map(({ formId }) => formId);
          // Always emit, even with an empty mainFormIds (e.g. right after a wizard's slots
          // are rebuilt and it has no forms yet). If we skipped the emit, switchMap below
          // would never see a "new" value, so it would keep its old subscription (watching
          // the now-deleted forms' state) alive instead of closing it - and that old
          // subscription is what throws "main form not found" once those forms' state
          // entries change.
          return { container, mainFormIds, relatedFormIds };
        }),
        switchMap(({ container, mainFormIds, relatedFormIds }) =>
          combineLatest(
            [...mainFormIds, ...relatedFormIds].map((formId) =>
              this.store.select(runtimeSelectors.selectFormStateById(formId)).pipe(
                distinctUntilChanged<FormState<FormStateContainer> | undefined>(isEqual),
                map((state) => ({ formId, state, isMain: mainFormIds.includes(formId) })),
              ),
            ),
          ).pipe(map((allFormsState) => ({ allFormsState, container }))),
        ),
      )
      .pipe(
        concatLatestFrom(() => this.store.select(getElementEntities)),
        map(([{ allFormsState, container }, elements]) => {
          if (!elements[container.id]) {
            return {
              containerId: container.id,
              containerType: container.type,
              mainForms: [],
              relatedForms: [],
            };
          }

          return {
            containerId: container.id,
            containerType: container.type,
            mainForms: allFormsState
              .filter(({ isMain }) => isMain)
              .map(({ formId, state }) => asMainForm(formId, state, elements[formId])),
            relatedForms: allFormsState
              .filter(({ isMain }) => !isMain)
              .map(({ formId, state }) => asRelatedForm(formId, state, elements[formId]))
              .filter(isDefined),
          };
        }),
      );
  }

  /**
   * Resolves the form group to observe and re-emits it whenever its wrapper changes, since
   * adding or removing a form changes which forms the group commits.
   */
  private observeFormGroupContainer(element: CanvasElement): Observable<CanvasElement | undefined> {
    return this.store.select(runtimeSelectors.selectIsRunning).pipe(
      filter((isRunning) => isRunning),
      concatLatestFrom(() => this.store.select(getElementEntities)),
      map(([, elements]) =>
        isVariantOfButton(element)
          ? Object.values(elements).find(
              (candidate) =>
                isFormGroup(candidate) &&
                hasWrapperRefs(candidate) &&
                candidate.props.wrapper.refs[FIELD_INFO_TYPE_COMMIT] === element.id,
            )
          : elements[element.id],
      ),
      filter(Boolean),
      switchMap((container) =>
        this.actions$.pipe(
          ofType(refreshFormGroup),
          filter(({ elementId }) => elementId === container.id),
          switchMap(({ elementId }) => this.store.select(getElementById(elementId)).pipe(take(1))),
          startWith(container),
        ),
      ),
    );
  }
}

function asMainForm(
  formId: CanvasElementId,
  state: FormState<FormStateContainer> | undefined,
  mainForm: CanvasElement | undefined,
): GroupedFormValidity<'formContainer' | 'formDetails'> {
  if (!mainForm) {
    throw new Error('main form not found');
  }
  if (mainForm.type !== 'formContainer' && mainForm.type !== 'formDetails') {
    throw new Error(`invalid main form type: ${mainForm.type}`);
  }
  return { id: formId, type: mainForm.type, state };
}

/** @returns `undefined` for the container types a group does not commit as a related form */
function asRelatedForm(
  formId: CanvasElementId,
  state: FormState<FormStateContainer> | undefined,
  relatedForm: CanvasElement | undefined,
): GroupedFormValidity<'formContainer' | 'editableList'> | undefined {
  if (!relatedForm) {
    throw new Error(`related form not found: ${formId}`);
  }
  if (
    relatedForm.type === 'formDetails' ||
    relatedForm.type === 'table' ||
    relatedForm.type === 'list'
  ) {
    return undefined;
  }
  if (relatedForm.type !== 'formContainer' && relatedForm.type !== 'editableList') {
    throw new Error('invalid related form type');
  }
  return { id: formId, type: relatedForm.type, state };
}
